/* article.js — page article « Tiitoune Aurum » (T18).
   Trois responsabilités, volontairement minimales :
   1. Sommaire : construit depuis les h2 du contenu (ids slugifiés si absents),
      ancres natives (défilement doux géré en CSS), section active suivie par
      IntersectionObserver (scrollspy).
   2. Barre de progression de lecture : largeur = avancée dans le corps de
      l'article, mise à jour via requestAnimationFrame, inactive si
      body.anim-off (et masquée en CSS).
   3. Partage : Web Share API avec repli copie de lien + bouton copie dédié
      (motif repris de product.js, autonome ici — product.js n'est pas chargé).
   Idempotent : init par racine via un drapeau dataset ; sur
   shopify:section:load la section est re-rendue (nœuds neufs, drapeau
   vierge), les anciens écouteurs partent avec les anciens nœuds. */
(function () {
  'use strict';

  /* Garde anti double-exécution (éditeur de thème, scripts dupliqués) */
  if (window.__aurumArticle) return;
  window.__aurumArticle = true;

  function noop() {}

  /* Slug d'ancre : minuscules, accents retirés, tirets */
  function slugify(text) {
    var slug = (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return slug || 'section';
  }

  /* Copie d'un lien avec retour visuel temporaire sur le bouton */
  function copyLink(button, statusNode) {
    if (!navigator.clipboard || !navigator.clipboard.writeText) return;
    var url = button.getAttribute('data-share-url') || window.location.href;
    navigator.clipboard.writeText(url).then(function () {
      var copied = button.getAttribute('data-label-copied');
      var labelNode = button.querySelector('[data-share-label], [data-copy-label]');
      if (statusNode && copied) statusNode.textContent = copied;
      if (labelNode && copied) {
        var original = labelNode.textContent;
        labelNode.textContent = copied;
        button.classList.add('is-copied');
        window.setTimeout(function () {
          labelNode.textContent = original;
          button.classList.remove('is-copied');
          if (statusNode) statusNode.textContent = '';
        }, 2000);
      }
    }).catch(noop);
  }

  function initArticle(root) {
    if (root.dataset.articleInit === '1') return;
    root.dataset.articleInit = '1';

    var body = root.querySelector('[data-article-body]');

    /* ----------------------------------------------------------------
       1. Sommaire — construit depuis les h2 du contenu (≥ 2 requis)
       ---------------------------------------------------------------- */
    var toc = root.querySelector('[data-toc]');
    var tocList = root.querySelector('[data-toc-list]');
    if (toc && tocList && body) {
      var headings = Array.prototype.slice.call(body.querySelectorAll('h2'));
      if (headings.length >= 2) {
        var usedIds = {};
        headings.forEach(function (heading) {
          if (!heading.id) {
            /* Id unique : suffixe numérique en cas de collision */
            var base = slugify(heading.textContent);
            var candidate = base;
            var suffix = 2;
            while (usedIds[candidate] || document.getElementById(candidate)) {
              candidate = base + '-' + suffix;
              suffix += 1;
            }
            heading.id = candidate;
          }
          usedIds[heading.id] = true;

          var item = document.createElement('li');
          item.className = 'article-toc__item';
          var link = document.createElement('a');
          link.className = 'article-toc__link';
          link.href = '#' + heading.id;
          link.textContent = heading.textContent;
          item.appendChild(link);
          tocList.appendChild(item);
        });
        toc.hidden = false;

        /* Scrollspy — la section dont le titre traverse le tiers haut
           de l'écran devient active dans le sommaire */
        if ('IntersectionObserver' in window) {
          var setActive = function (id) {
            Array.prototype.forEach.call(
              tocList.querySelectorAll('.article-toc__link'),
              function (link) {
                var active = link.getAttribute('href') === '#' + id;
                link.classList.toggle('is-active', active);
                if (active) {
                  link.setAttribute('aria-current', 'true');
                } else {
                  link.removeAttribute('aria-current');
                }
              }
            );
          };
          var spy = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) setActive(entry.target.id);
            });
          }, { rootMargin: '-20% 0px -70% 0px' });
          headings.forEach(function (heading) { spy.observe(heading); });
        }
      }
    }

    /* ----------------------------------------------------------------
       2. Barre de progression de lecture — rAF, inactive si anim-off
       ---------------------------------------------------------------- */
    var progressBar = root.querySelector('[data-progress-bar]');
    if (progressBar && body) {
      var ticking = false;
      var update = function () {
        ticking = false;
        if (!progressBar.isConnected) {
          window.removeEventListener('scroll', onScroll);
          return;
        }
        if (document.body.classList.contains('anim-off')) return;
        var rect = body.getBoundingClientRect();
        var travel = rect.height - window.innerHeight;
        var progress;
        if (travel > 0) {
          progress = Math.min(1, Math.max(0, -rect.top / travel));
        } else {
          progress = rect.top < 0 ? 1 : 0;
        }
        progressBar.style.transform = 'scaleX(' + progress + ')';
      };
      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }

    /* ----------------------------------------------------------------
       3. Partage — délégation sur la racine de la section
       ---------------------------------------------------------------- */
    root.addEventListener('click', function (event) {
      var statusNode = root.querySelector('[data-share-status]');

      /* Copie de lien dédiée */
      var copyBtn = event.target.closest('[data-copy-link]');
      if (copyBtn) {
        copyLink(copyBtn, statusNode);
        return;
      }

      /* Web Share API, sinon repli copie */
      var shareBtn = event.target.closest('[data-share-button]');
      if (shareBtn) {
        var url = shareBtn.getAttribute('data-share-url') || window.location.href;
        var title = shareBtn.getAttribute('data-share-title') || document.title;
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(noop);
          return;
        }
        copyLink(shareBtn, statusNode);
      }
    });
  }

  function initAll(scope) {
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-article-root]'),
      initArticle
    );
  }

  /* Script chargé en defer : le DOM est prêt */
  initAll(document);

  /* Éditeur de thème : la section re-rendue arrive avec des nœuds neufs */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
